import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { getCurrentLanguage, Languages } from '../i18n/language'
import styled from 'styled-components'
import { abcSrc } from './resources/abcLogo'
import { mobile, tablet } from '../common/style/media-queries'
import i18next from 'i18next'
import { socialMediasConfig } from '../home/contact/social-medias'
import { BlueSectionWrapper, TopSeparator } from '../common/style/Section'
import { NewTabLink, InternalLink } from '../common/style/components/links'
import { CookiesDisclosure } from './CookiesDisclosure'
import { Address } from '../home/contact/addresses'

const Footer: React.FC<{ topColor?: string }> = ({ topColor = '#FFFFFF' }) => {
  const [t] = useTranslation(['footer', 'menu'])
  const addresses: Address[] = t('addresses', { returnObjects: true })
  const legalDocs: Array<{ label: string; link: string; event: string }> = t(
    'legal_docs',
    {
      returnObjects: true,
    }
  )
  return (
    <footer>
      <BlueSectionWrapper>
        <TopSeparator viewBox="0 0 1201 68">
          <g stroke="none" fillRule="evenodd" fill={topColor}>
            <path d="M0,0H1201V68S601,17,441,18s-206,3-274,9S0,45,0,45Z" />
          </g>
        </TopSeparator>
        <ImageWrapper>
          <img alt="" {...abcSrc} />
        </ImageWrapper>
        <FooterContent>
          <FooterColumn>
            <h4>{t('contact_us')}</h4>
            {addresses[0].email}
            <br />
            {addresses.map((address: Address) => (
              <AddressWrapper>
                <b>{address.place}</b>
                {address.streetAddressLine1}
                <br />
                {address.streetAddressLine2}
              </AddressWrapper>
            ))}
          </FooterColumn>
          <FooterColumn>
            <h4>{t('information')}</h4>
            <NewTabLink to={t('faq_link')} eventLabel="footer click faq">
              {t('faq')}
            </NewTabLink>
            <InternalLink to="/team">{t('menu:team')}</InternalLink>
            {getCurrentLanguage() === Languages.fr && (
              <NewTabLink
                to="https://www.welcometothejungle.co/fr/companies/lalilo"
                eventLabel="footer hiring"
              >
                {t('menu:join')}
              </NewTabLink>
            )}
          </FooterColumn>
          <DesktopFooterColumn>
            <h4>{t('language')}</h4>
            <NavLink onClick={() => i18next.changeLanguage('fr')}>
              {t('french')}
            </NavLink>
            <NavLink onClick={() => i18next.changeLanguage('en')}>
              {t('english')}
            </NavLink>
          </DesktopFooterColumn>
          <FooterColumn>
            <div>
              <DesktopTitle>{t('follow_us')}</DesktopTitle>
              {socialMediasConfig.map(media => (
                <SocialMediaLink
                  to={media.href}
                  key={media.href}
                  eventLabel={`footer ${media.gaLabel}`}
                >
                  <img alt={media.name} src={media.srcFooter} />
                </SocialMediaLink>
              ))}
            </div>
          </FooterColumn>
        </FooterContent>
        <Copyright>
          © Copyright 2020 lalilo
          {legalDocs.map(({ link, label, event }) => (
            <>
              {' | '}
              <NewTabLink to={link} eventLabel={event}>
                {label}
              </NewTabLink>
            </>
          ))}
        </Copyright>
      </BlueSectionWrapper>
      <CookiesDisclosure />
    </footer>
  )
}

export default Footer

const ImageWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  z-index: 10;
  top: 13px;
  left: 26%;
  height: 0;
  @media ${mobile} {
    display: none;
  }
  @media ${tablet} {
    display: none;
  }
`

const FooterContent = styled.div`
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  padding: 0 20px;
  color: #ffffff;
  font-size: 12px;
  line-height: 2.5;
  margin-top: 70px;
  @media ${mobile} {
    flex-direction: column;
    margin-top: 30px;
  }
`

const FooterColumn = styled.div`
  width: 250px;
  display: flex;
  flex: 1;
  flex-direction: column;
  text-align: center;
  @media ${mobile} {
    margin-bottom: 20px;
    width: 100%;
    justify-content: center;
    align-content: center;
    align-items: center;
  }
`

const AddressWrapper = styled.div`
  line-height: 1.5;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 10px;
`

const NavLink = styled.div`
  cursor: pointer;
`

const DesktopFooterColumn = styled(FooterColumn)`
  @media ${mobile} {
    display: none;
  }
`

const DesktopTitle = styled.h4`
  @media ${mobile} {
    display: none;
  }
`

const SocialMediaLink = styled(NewTabLink)`
  position: relative;
  width: 40px;
  margin: 3px;
  top: 20px;
`

const Copyright = styled.div`
  margin-top: 40px;
  margin-bottom: 30px;
  font-size: 12px;
  color: #ffffff;
`
